/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Material UI table
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  MenuItem,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Close";

import { getData, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import { commonHeaderLinkLists } from "helper/PatientManagementHelper/PatientManagementHelper";
import AccessDeniedView from "components/Common/AccessDeniedView/AccessDeniedView";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import {
  getDateFormatInSpanish,
  mergeListById,
} from "helper/Common/CommonHelper";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";
import { assetImages } from "constants";
import DeleteWarningModal from "components/Common/Modal/DeleteWarningModal";
import AssignUserModal from "components/Common/Modal/AssignUserModal";
import TemperatureHeader from "./Header/TemperatureHeader"; // Changed from BloodPressureHeader
import VitalCommonheader from "../Common/Header/VitalCommonheader";
import TemperatureFilter from "./Popup/TemperatureFilter";
import { removeTagHandler } from "helper/TagHelper/TagHelper";
import AddLabelModal from "components/Common/Modal/AddLabelModal";
import SaveTemperaturePopup from "components/Common/Popup/SaveTemperaturePopup";

const TemperatureBody = () => {
  const { t } = useTranslation();

  const token = localStorage.getItem("token");
  const moduleAccess = localStorage.getItem("moduleaccess");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  const [isLoading, setIsLoading] = useState(false);
  const [temperatureList, setTemperatureList] = useState([]); // Changed from bloodPressureList
  const [rowSelection, setRowSelection] = useState({});
  const [selectedTemperatureIds, setSelectedTemperatureIds] = useState([]); // Changed from selectedBloodPressureIds
  const [selectedTemperatureId, setSelectedTemperatureId] = useState(null); // Changed from selectedBloodPressureId
  const [selectedTagId, setSelectedTagId] = useState(null);

  // json filter query
  const [jsonFilterQuery, setJsonFilterQuery] = useState([]);
  const [isFilterReset, setIsFilterReset] = useState(false);
  const [reloadData, setReloadData] = useState(false);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const [isDataReady, setIsDataReady] = useState(false);

  //get all temperature
  const getAllTemperature = async () => {
    try {
      setIsLoading(true);
      let requestUrl =
        url.API_BASE_URL + url.API_GET_ALL_TEMPERATURE + `?token=${token}`; // Changed API URL

      if (jsonFilterQuery.length > 0) {
        // Sanitize the jsonFilterQuery
        const sanitizedQuery = jsonFilterQuery.map((filter) => {
          if (
            (filter.operator === "includes" ||
              filter.operator === "excludes") &&
            Array.isArray(filter.value)
          ) {
            return {
              ...filter,
              value: filter.value.map((item) => item.value).join(","), // Extract and join values as a comma-separated string
            };
          }
          return filter;
        });

        requestUrl = requestUrl + `&filters=${JSON.stringify(sanitizedQuery)}`;
      }

      const response = await getData(requestUrl);

      setIsLoading(false);

      if (response.status) {
        const { listrecords } = response.data;
        setTemperatureList(listrecords); // Changed from setBloodPressureList
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  //update temperature
  const updateBulkTemperatureHandler = async () => {
    if (selectedTemperatureIds.length > 0) {
      try {
        const temperatureListToBeUpdate = temperatureList.filter((item) =>
          selectedTemperatureIds.includes(item._id)
        );

        let temperatureData = {
          temperaturelist: temperatureListToBeUpdate,
        };

        let requestURL =
          url.API_BASE_URL +
          url.API_UPDATE_BULK_TEMPERATURE + // Changed API URL
          `?token=${token}`;

        const response = await putData(requestURL, temperatureData);

        console.log(response);

        if (response.status) {
          setMessageType("success");

          setTimeout(() => {
            setSelectedTemperatureIds([]);
            setRowSelection({});
          }, 1500);
        } else {
          setMessageType("error");
        }

        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one temperature");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  //delete temperature
  const deleteBulkTemperatureHandler = async () => {
    if (selectedTemperatureIds.length > 0) {
      try {
        const temperatureListToBeDelete = temperatureList.filter((item) =>
          selectedTemperatureIds.includes(item._id)
        );

        let temperatureData = {
          temperaturelist: temperatureListToBeDelete,
        };

        let requestURL =
          url.API_BASE_URL +
          url.API_DELETE_BULK_TEMPERATURE + // Changed API URL
          `?token=${token}`;

        const response = await putData(requestURL, temperatureData);

        console.log(response);

        if (response.status) {
          setMessageType("success");

          setTimeout(() => {
            setTemperatureList((prevList) =>
              prevList.filter(
                (item) => !selectedTemperatureIds.includes(item._id)
              )
            );

            setSelectedTemperatureIds([]);
            setRowSelection({});
          }, 1500);
        }

        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one temperature");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  //remove tag from temperature
  const removeTagFromTemperature = async (tagIds, componentId) => {
    try {
      const componentUrl = url.API_ADD_REMOVE_TAG_TEMPERATURE; // Changed API URL
      const data = await removeTagHandler(tagIds, componentId, componentUrl);

      if (data) {
        afterActionClose(data);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setTimeout(() => {
        setSelectedTagId(null);
        setSelectedTemperatureId(null);
      }, 1000);
    }
  };

  const afterActionClose = (data = []) => {
    setTemperatureList((prevList) => mergeListById(prevList, data)); // Changed from setBloodPressureList
  };

  const resetFilterData = () => {
    setJsonFilterQuery([]);
    setIsFilterReset(true);
    setReloadData(true);
    setRowSelection({});
    setSelectedTemperatureIds([]); // Changed from setSelectedBloodPressureIds
  };

  useEffect(() => {
    if (reloadData) {
      getAllTemperature();
      setReloadData(false);
      setIsDataReady(true);
    }
  }, [reloadData]);

  useEffect(() => {
    if (jsonFilterQuery.length > 0) {
      getAllTemperature();
      setIsDataReady(true);
    }
  }, [jsonFilterQuery]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: t("Actions"),
        size: 70,
        Cell: ({ row }) => (
          <button
            className="action_btn_mui"
            data-bs-toggle="offcanvas"
            data-bs-target="#saveTemperatureOffCanvas" // Changed from #saveBloodPressureOffCanvas
            aria-controls="saveTemperatureOffCanvas" // Changed from saveBloodPressureOffCanvas
            onClick={() => {
              setSelectedTemperatureId(row.original._id); // Changed from setSelectedBloodPressureId
            }}
          >
            <span className="d-block material-symbols-outlined horz_icon">
              more_horiz
            </span>
          </button>
        ),
        enableColumnActions: false, // Hides the column action icon
        enableColumnDragging: false, // Hides the move icon
        enableSorting: false,
      },
      {
        header: "Celsius",
        accessorKey: "celcius",
        size: 200,
      },
      //tags column
      {
        accessorKey: "tagstring",
        header: t("Tags"),
        size: 200,
        Cell: ({ row }) => (
          <div className="label-wrapper py-1">
            <div className="d-flex gap-2">
              <div
                className={
                  row.original.taglist.length > 0
                    ? "label-container flex-grow-1"
                    : "d-none"
                }
              >
                <ul
                  className="d-flex flex-wrap gap-2 m-0 p-0"
                  style={{ listStyle: "none" }}
                >
                  {row.original.taglist.map((tag, index) => (
                    <li key={index} className="d-inline-flex">
                      <div className="badge d-flex align-items-center px-3 py-2 border-0 gradient-light text-black rounded-pill">
                        <span className="badge-text fs-xs fw-medium me-1">
                          {tag.title}
                        </span>
                        <Link
                          to="#"
                          className={`${
                            tag.moderator.toString() ===
                              userInfo._id.toString() ||
                            userInfo.role.slug === "ADMIN" ||
                            userInfo.role.slug === "SUPER_ADMIN"
                              ? "d-block"
                              : "d-none"
                          }`}
                          onClick={() => {
                            setSelectedTemperatureId(row.original._id);
                            setSelectedTagId(tag._id);
                            removeTagFromTemperature(
                              [tag._id],
                              row.original._id
                            );
                          }}
                        >
                          {selectedTagId &&
                          selectedTagId === tag._id &&
                          selectedTemperatureId &&
                          selectedTemperatureId === row.original._id ? (
                            <div
                              className="spinner-border spinner-border-sm text-primary ms-1"
                              role="status"
                              style={{ height: "12px", width: "12px" }}
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          ) : (
                            <span
                              className="d-block text-primary material-symbols-outlined icon-sm ms-1"
                              style={{ cursor: "pointer" }}
                            >
                              cancel
                            </span>
                          )}
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                className="btn btn-light btn-sm rounded-circle p-1 shadow-sm border-0 d-flex align-items-center justify-content-center mt-1"
                style={{ width: "24px", height: "24px" }}
                data-bs-toggle="modal"
                data-bs-target="#addLabelModal"
                onClick={() => setSelectedTemperatureIds([row.original._id])}
              >
                <span
                  className="material-symbols-outlined text-primary"
                  style={{ fontSize: "16px" }}
                >
                  add
                </span>
              </button>
            </div>
          </div>
        ),
      },
      {
        header: "Fahrenheit",
        accessorKey: "fahrenheit",
        size: 200,
      },
      {
        header: "Date",
        accessorKey: "date",
        Cell: ({ row }) => <p>{getDateFormatInSpanish(row.original.date)}</p>,
      },
      {
        header: "Assigned To",
        accessorKey: "username",
        size: 300,
        Cell: ({ row }) => (
          <div
            className={
              row.original.username === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.userimage == ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.userimage
              }
              alt="Babcock"
            />
            <p>
              <span className="d-block">{row.original.username}</span>
            </p>
          </div>
        ),
      },
    ],
    [selectedTagId, selectedTemperatureId, selectedTemperatureIds]
  );

  //initialize the column order
  const columnOrder = [
    "#",
    "mrt-row-select",
    ...columns.map((c) => c.accessorKey),
  ]; //array of column ids (Initializing is optional as of v2.10.0)

  useEffect(() => {
    const selectedIdsArray = Object.keys(rowSelection).filter(
      (key) => rowSelection[key]
    );

    setSelectedTemperatureIds(selectedIdsArray); // Changed from setSelectedBloodPressureIds
  }, [rowSelection]);

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  if (
    userInfo.role.slug === "ADMIN" ||
    userInfo.role.slug === "SUPER_ADMIN" ||
    (moduleAccess.includes("MOD_PATIENT_MANAGEMENT") &&
      moduleAccess.includes("MOD_VITAL_MANAGEMENT"))
  ) {
    return (
      <div id="content_wrapper">
        <section className="crm-wrapper crm-conversation-wrapper bg-white pb-5">
          <TabsHeader
            commonHeaderObject={commonHeaderLinkLists(t)}
            activeOption={t("Vitals")}
          />
          <div className="container-fluid px-lg-5">
            <TemperatureHeader // Changed from BloodPressureHeader
              selectedItemIds={selectedTemperatureIds} // Changed from selectedBloodPressureIds
              updateBulkHandler={updateBulkTemperatureHandler} // Changed from updateBulkBloodPressureHandler
              reloadList={resetFilterData} // Changed from getAllBloodPressure
              jsonFilterQuery={jsonFilterQuery}
            />
            <VitalCommonheader activeOption="temperature" />{" "}
            {/* Changed activeOption */}
            {isLoading || !isDataReady ? (
              <LoadingSkeleton />
            ) : (
              <div className="table-wrapper">
                <MaterialReactTable
                  columns={columns}
                  data={temperatureList} // Changed from bloodPressureList
                  enableGrouping // to enable grouping of column
                  enableRowSelection // enable showing checkbox in each row
                  getRowId={(row) => row._id}
                  onRowSelectionChange={setRowSelection}
                  state={{ rowSelection, columnOrder }}
                  initialState={{ rowSelection, columnOrder }} // to set the initial state of column order
                  defaultColumn={{
                    minSize: 20,
                    maxSize: 300,
                    size: 50, //make columns wider by default
                  }}
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: "60vh",
                    },
                  }}
                  enableStickyHeader
                />
              </div>
            )}
          </div>

          {/* modal and popup section  */}

          <SaveTemperaturePopup
            selectedTemperatureId={selectedTemperatureId} // Changed from selectedBloodPressureId
            setSelectedTemperatureId={setSelectedTemperatureId} // Changed from setSelectedBloodPressureId
            afterActionClose={afterActionClose}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
          />

          <AssignUserModal
            moduleName="temperature" // Changed moduleName
            afterModalClose={afterActionClose}
            selectedIds={selectedTemperatureIds} // Changed from selectedBloodPressureIds
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
            onModalClose={() => {
              setRowSelection({});
              setSelectedTemperatureIds([]); // Changed from setSelectedBloodPressureIds
            }}
          />

          <TemperatureFilter
            moduleSlug="temperaturelist"
            jsonFilterQuery={jsonFilterQuery}
            setJsonFilterQuery={setJsonFilterQuery}
            reloadList={resetFilterData}
            isFilterReset={isFilterReset}
            setIsFilterReset={setIsFilterReset}
          />

          <AddLabelModal
            moduleName="temperature" // Changed moduleName
            selectedIds={selectedTemperatureIds} // Changed from selectedBloodPressureIds
            afterTagModalClose={afterActionClose}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
            onModalClose={() => {
              setRowSelection({});
              setSelectedTemperatureIds([]); // Changed from setSelectedBloodPressureIds
            }}
          />

          <DeleteWarningModal
            onDelete={deleteBulkTemperatureHandler} // Changed from deleteBulkBloodPressureHandler
            onCancelDelete={() => {
              setRowSelection({});
              setSelectedTemperatureIds([]); // Changed from setSelectedBloodPressureIds
            }}
            moduleName={t("temperature")} // Changed moduleName
          />

          {showAlert && (
            <AlertNotification
              showAlert={showAlert}
              message={alertMessage}
              alertType={messageType}
              onClose={onAlertClose}
            />
          )}
        </section>
      </div>
    );
  } else {
    return <AccessDeniedView />;
  }
};

export default TemperatureBody;
